import { v4 as uuidv4 } from 'uuid';
import { apiService } from './api';

export interface GlossaryEntry {
  id: string;
  term: string;
  definition: string;
  category?: string;
  aliases?: string[];
  tags?: string[];
  image?: string; // Optional image URL or base64 data
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export interface GlossaryImportData {
  entries: Omit<GlossaryEntry, 'id' | 'createdAt' | 'updatedAt' | 'isActive'>[];
}

export interface GlossaryExportData {
  entries: GlossaryEntry[];
  exportedAt: string;
  version: string;
}

class GlossaryService {
  private entries: GlossaryEntry[] = [];
  private loadedFromAPI: boolean = false;
  private loadingPromise: Promise<void> | null = null;

  constructor() {
    // Always load from API on initialization
    this.loadFromAPI();
  }

  private async loadFromAPI(): Promise<void> {
    // Prevent multiple simultaneous API calls
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this._loadFromAPI();
    return this.loadingPromise;
  }

  private async _loadFromAPI(): Promise<void> {
    try {
      console.log('Loading glossary from API (approved items only for highlighting)...');
      // Use forHighlighting=true to only get approved items
      const response = await apiService.getGlossary(false, true);
      if (response.success && response.data && Array.isArray(response.data)) {
        console.log(`Loaded ${response.data.length} approved glossary items from API`);
        this.entries = response.data;
        this.loadedFromAPI = true;
      } else {
        console.warn('API returned no data, using empty glossary');
        this.entries = [];
        this.loadedFromAPI = true;
      }
    } catch (error) {
      console.error('Error loading glossary from API:', error);
      // Use empty array if API fails - no fallback to localStorage
      this.entries = [];
      this.loadedFromAPI = true;
    } finally {
      this.loadingPromise = null;
    }
  }

  // Get all entries - always ensure API data is loaded
  async getAllEntries(): Promise<GlossaryEntry[]> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }
    return [...this.entries];
  }

  // Get active entries only - always ensure API data is loaded
  async getActiveEntries(): Promise<GlossaryEntry[]> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }
    return this.entries.filter(entry => entry.isActive);
  }

  // Method to manually refresh from API
  async refreshFromAPI(): Promise<void> {
    this.loadedFromAPI = false;
    await this.loadFromAPI();
  }

  // Get entry by ID - ensure data is loaded
  async getEntryById(id: string): Promise<GlossaryEntry | undefined> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }
    return this.entries.find(entry => entry.id === id);
  }

  // Add new entry - this would need backend API support
  addEntry(entryData: Omit<GlossaryEntry, 'id' | 'createdAt' | 'updatedAt'>): GlossaryEntry {
    const now = new Date().toISOString();
    const newEntry: GlossaryEntry = {
      ...entryData,
      id: uuidv4(),
      createdAt: now,
      updatedAt: now
    };

    this.entries.push(newEntry);
    return newEntry;
  }

  // Update existing entry - this would need backend API support
  updateEntry(id: string, updates: Partial<Omit<GlossaryEntry, 'id' | 'createdAt'>>): boolean {
    const index = this.entries.findIndex(entry => entry.id === id);
    if (index === -1) return false;

    this.entries[index] = {
      ...this.entries[index],
      ...updates,
      updatedAt: new Date().toISOString()
    };

    return true;
  }

  // Delete entry - this would need backend API support
  deleteEntry(id: string): boolean {
    const initialLength = this.entries.length;
    this.entries = this.entries.filter(entry => entry.id !== id);
    return this.entries.length < initialLength;
  }

  // Bulk delete entries - this would need backend API support
  deleteEntries(ids: string[]): number {
    const initialLength = this.entries.length;
    this.entries = this.entries.filter(entry => !ids.includes(entry.id));
    return initialLength - this.entries.length;
  }

  // Toggle entry active status - this would need backend API support
  toggleEntryStatus(id: string): boolean {
    const entry = this.entries.find(e => e.id === id);
    if (!entry) return false;

    entry.isActive = !entry.isActive;
    entry.updatedAt = new Date().toISOString();
    return true;
  }

  // Search entries - ensure data is loaded
  async searchEntries(query: string, filters?: {
    category?: string;
    tags?: string[];
    isActive?: boolean;
  }): Promise<GlossaryEntry[]> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }

    let results = [...this.entries];

    // Apply filters
    if (filters?.category) {
      results = results.filter(entry => entry.category === filters.category);
    }

    if (filters?.tags && filters.tags.length > 0) {
      results = results.filter(entry => 
        entry.tags?.some(tag => filters.tags!.includes(tag))
      );
    }

    if (filters?.isActive !== undefined) {
      results = results.filter(entry => entry.isActive === filters.isActive);
    }

    // Apply search query
    if (query.trim()) {
      const searchTerm = query.toLowerCase();
      results = results.filter(entry => 
        entry.term.toLowerCase().includes(searchTerm) ||
        entry.definition.toLowerCase().includes(searchTerm) ||
        entry.aliases?.some(alias => alias.toLowerCase().includes(searchTerm)) ||
        entry.tags?.some(tag => tag.toLowerCase().includes(searchTerm))
      );
    }

    return results;
  }

  // Get all categories - ensure data is loaded
  async getCategories(): Promise<string[]> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }
    const categories = new Set(this.entries.map(entry => entry.category).filter(Boolean));
    return Array.from(categories).sort();
  }

  // Get all tags - ensure data is loaded
  async getTags(): Promise<string[]> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }
    const tags = new Set(
      this.entries
        .flatMap(entry => entry.tags || [])
        .filter(Boolean)
    );
    return Array.from(tags).sort();
  }

  // Import entries from JSON - this would need backend API support
  importEntries(data: GlossaryImportData): { success: number; errors: string[] } {
    const errors: string[] = [];
    let successCount = 0;

    for (const entryData of data.entries) {
      try {
        // Validate required fields
        if (!entryData.term || !entryData.definition) {
          errors.push(`Entry missing required fields: ${entryData.term || 'Unknown'}`);
          continue;
        }

        // Check for duplicates
        const existingEntry = this.entries.find(entry => 
          entry.term.toLowerCase() === entryData.term.toLowerCase()
        );

        if (existingEntry) {
          errors.push(`Duplicate entry: ${entryData.term}`);
          continue;
        }

        // Add the entry
        this.addEntry({
          ...entryData,
          isActive: true
        });
        successCount++;

      } catch (error) {
        errors.push(`Error importing ${entryData.term}: ${error}`);
      }
    }

    return { success: successCount, errors };
  }

  // Export entries to JSON - ensure data is loaded
  async exportEntries(): Promise<GlossaryExportData> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }
    return {
      entries: this.entries,
      exportedAt: new Date().toISOString(),
      version: '1.0.0'
    };
  }

  // Clear all entries - this would need backend API support
  clearAllEntries(): void {
    this.entries = [];
  }

  // Get statistics - ensure data is loaded
  async getStatistics(): Promise<{
    total: number;
    active: number;
    inactive: number;
    categories: number;
    tags: number;
  }> {
    if (!this.loadedFromAPI) {
      await this.loadFromAPI();
    }
    const categories = await this.getCategories();
    const tags = await this.getTags();
    
    return {
      total: this.entries.length,
      active: this.entries.filter(e => e.isActive).length,
      inactive: this.entries.filter(e => !e.isActive).length,
      categories: categories.length,
      tags: tags.length
    };
  }
}

export const glossaryService = new GlossaryService();
export default glossaryService;
